import Tab from '@jetbrains/ring-ui/components/tabs/tab'
import {Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import classNames from 'classnames'
import {Suspense, useContext, useMemo} from 'react'
import {EntryPointContainer} from 'react-relay'

import {useQueryParam} from '../../../../../../hooks/routes'
import {getHrefWithQueryParams} from '../../../../../../routes'
import {BuildPageTabNamesEnum} from '../../../../../../types'
import type {BuildId, Tab as TabType, TabId} from '../../../../../../types'
import type {PreloadedEntryPoints} from '../../../../../../types/entryPoints'
import {emptyArray, noop} from '../../../../../../utils/empty'
import Loader from '../../../../../common/Loader/Loader'
import {BuildTabs} from '../../../../../common/ToggleSakuraUI/ToggleSakuraUI.utils'
import BuildLogTab from '../../../../../packages/BuildLog/BuildLogTab/BuildLogTab.container'
import {SELECTED_BUILD_DEPENDENCY_ID_PARAM} from '../../../../../packages/BuildLog/FullBuildLog/BuildLogBuildNavigationWrapper/BuildLogBuildNavigationWrapper.consts'
import {PipelinesTarget, PipelinesTargetContext} from '../../../contexts/targetContext'
import type {NestedEntryPoints} from '../../PipelineRunPage'

import GameTab from './GameTab/GameTab'
import {useJobGameTabContext} from './GameTab/GameTab.hooks'
import {
  additionalJobDetailsTabs,
  AdditionalJobDetailsTabs,
  supportedTabs,
} from './JobDetails.consts'
import useJobDetails from './JobDetails.hooks'
import JobDetailsHeader from './JobDetailsHeader/JobDetailsHeader'

import styles from './JobDetails.css'

type Props = {
  className?: string
  jobId: BuildId
  entryPoints: PreloadedEntryPoints<NestedEntryPoints>
}

function JobDetails({className, jobId, entryPoints}: Props) {
  const jobGameTabContext = useJobGameTabContext()
  const pipelineTarget = useContext(PipelinesTargetContext)
  const pageFullLogBuildIdParam = useQueryParam(SELECTED_BUILD_DEPENDENCY_ID_PARAM)
  const id = Number(pageFullLogBuildIdParam ?? jobId)
  const {searchParams, navigate, location, isPipelineFinished, isLoading, tabs} =
    useJobDetails(jobId)
  const selectedTabInURL = searchParams.get('tab')

  const allTabs = useMemo(() => {
    if (tabs == null || tabs.length === 0) {
      return emptyArray
    }
    return supportedTabs.reduce<TabType[]>((acc, tabId) => {
      let tab = tabs?.find(tab => tabId === BuildTabs[tab.id])

      if (
        tabId === AdditionalJobDetailsTabs.GAME &&
        (!isPipelineFinished || selectedTabInURL === AdditionalJobDetailsTabs.GAME)
      ) {
        tab = additionalJobDetailsTabs[AdditionalJobDetailsTabs.GAME]
      }

      return tab != null ? [...acc, tab] : acc
    }, [])
  }, [tabs, isPipelineFinished, selectedTabInURL])

  const selectedTab = selectedTabInURL ?? allTabs[0]?.id

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <Loader />
      </div>
    )
  }

  const renderTabContent = (tabId: TabId) => {
    if (BuildTabs[tabId] === BuildPageTabNamesEnum.LOG) {
      return <BuildLogTab className={styles.logTab} buildId={jobId} />
    }
    if (BuildTabs[tabId] === BuildPageTabNamesEnum.CHANGES && entryPoints.changesTab) {
      return (
        <EntryPointContainer entryPointReference={entryPoints.changesTab} props={{jobId: id}} />
      )
    }

    if (BuildTabs[tabId] === BuildPageTabNamesEnum.TESTS && entryPoints.testsTab) {
      return (
        <EntryPointContainer entryPointReference={entryPoints.testsTab} props={{buildId: id}} />
      )
    }
    if (BuildTabs[tabId] === BuildPageTabNamesEnum.ARTIFACTS && entryPoints.artifactsTab) {
      return (
        <EntryPointContainer entryPointReference={entryPoints.artifactsTab} props={{buildId: id}} />
      )
    }
    if (tabId === AdditionalJobDetailsTabs.GAME) {
      return <GameTab />
    }
    return null
  }

  return (
    <div
      className={classNames(className, styles.wrapper, {
        [styles.jobDetailsPopupTarget]: pipelineTarget === PipelinesTarget.JOB_DETAILS_POPUP,
        [styles.jobDetailsSidebarTarget]: pipelineTarget === PipelinesTarget.JOB_DETAILS_SIDEBAR,
      })}
    >
      <JobDetailsHeader className={styles.header} jobId={jobId} />
      <Tabs
        className={styles.tabs}
        tabContainerClassName={styles.tabContainer}
        selected={selectedTab}
      >
        {allTabs.map(tab => {
          const isGameTab = tab.id === AdditionalJobDetailsTabs.GAME
          const href = getHrefWithQueryParams(location, null, params => ({...params, tab: tab.id}))
          return (
            <Tab
              className={classNames(styles.tab, {
                [styles.gameTab]: isGameTab,
                [styles.hideGameTabTitle]: isGameTab && !jobGameTabContext.wasFound,
              })}
              key={tab.id}
              id={tab.id}
              title={tab.title}
              href={href}
              titleProps={{
                onClick: isGameTab ? () => jobGameTabContext.setWasFound(true) : noop,
                onPlainLeftClick: () => navigate(href, {replace: selectedTab === tab.id}),
              }}
            >
              <Suspense fallback={<Loader />}>{renderTabContent(tab.id)}</Suspense>
            </Tab>
          )
        })}
      </Tabs>
    </div>
  )
}

export default JobDetails
